/* eslint-disable @typescript-eslint/no-explicit-any */
import { Textbox } from 'fabric';

import {
  TextEntityType,
  TextEntityOptions,
  isBulletType,
  getBulletLevel,
  getBulletTypeForLevel,
} from '../types/fabric-extensions';

/**
 * Кастомный класс TextEntity наследующийся от Textbox
 * Основа системы метабуллитов с встроенной логикой
 */
export class TextEntity extends Textbox {
  // Кастомные свойства метабуллитов
  id: string;
  entityType: TextEntityType;
  metaBulletId: string;
  entityOrder: number;
  bulletLevel?: number;
  isMetaBulletEntity: boolean;
  marginBottom?: number;
  marginLeft?: number;
  excludeFromBackup?: boolean;

  // Статический тип для регистрации в Fabric.js
  static type = 'textEntity';

  constructor(text: string, options: TextEntityOptions = {}) {
    // Получаем дефолтные стили для типа сущности
    const entityType = options.entityType || 'paragraph';
    const defaultStyles = TextEntity.getDefaultStylesForType(entityType);

    // Объединяем дефолтные стили с переданными опциями
    const mergedOptions = {
      ...defaultStyles,
      ...options,
      // Гарантируем обязательные значения чтобы избежать undefined
      fontFamily: options.fontFamily || defaultStyles.fontFamily || 'Arial',
      fontSize: options.fontSize || defaultStyles.fontSize || 16,
      fill: options.fill || defaultStyles.fill || '#000000',
      // Гарантируем что это метабуллит объект
      isMetaBulletEntity: true,
    };

    // Фильтруем опции для родительского конструктора - убираем кастомные поля
    const fabricOptions = {
      left: mergedOptions.left,
      top: mergedOptions.top,
      width: mergedOptions.width,
      height: mergedOptions.height,
      fontSize: mergedOptions.fontSize,
      fontFamily: mergedOptions.fontFamily,
      fontWeight: mergedOptions.fontWeight,
      fill: mergedOptions.fill,
      textAlign: mergedOptions.textAlign,
      lineHeight: mergedOptions.lineHeight,
    };

    // Убираем undefined значения
    const cleanedOptions = Object.fromEntries(
      Object.entries(fabricOptions).filter(([, value]) => value !== undefined),
    );

    // Вызываем родительский конструктор только со стандартными опциями
    super(text, cleanedOptions as any);

    // Устанавливаем кастомные свойства
    this.id = options.id || '';
    this.entityType = entityType;
    this.metaBulletId = options.metaBulletId || '';
    this.entityOrder = options.entityOrder || 0;
    this.isMetaBulletEntity = true;
    this.excludeFromBackup = options.excludeFromBackup || false;

    // Если это буллит, устанавливаем уровень и отступы
    if (isBulletType(entityType)) {
      this.bulletLevel = options.bulletLevel || getBulletLevel(entityType);
      this.marginLeft = options.marginLeft || this.calculateIndentForLevel(this.bulletLevel);
    }

    // Устанавливаем нижний отступ
    this.marginBottom = options.marginBottom || defaultStyles.marginBottom;

    // Подписываемся на событие изменения текста через canvas
    // Это нужно делать после добавления объекта на canvas
    this.on('added', () => {
      if (this.canvas) {
        this.canvas.on('text:changed', (e) => {
          if (e.target === this) {
            this._onTextChanged();
          }
        });
      }
    });

    // Также подписываемся на событие modified для более надежного отслеживания изменений
    this.on('modified', () => {
      console.log('📝 TextEntity.modified event:', {
        id: this.id,
        entityType: this.entityType,
        textLength: this.text?.length || 0,
        position: { x: this.left, y: this.top },
        metaBulletId: this.metaBulletId,
      });

      // Проверяем состояние drag'а перед отправкой события
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const system = (this.canvas as any)?._metaBulletSystem;

      // Если мы находимся в процессе drag'а этой сущности, проверяем не вышли ли за границы
      if (system && system.currentDraggedEntity && system.currentDraggedEntity.id === this.id) {
        console.debug('🔍 Entity is being dragged, checking bounds:', {
          entityId: this.id,
          isOutOfBounds: system.isCurrentlyDragOutOfBounds,
        });

        // Если вышли за границы, не отправляем уведомление
        if (system.isCurrentlyDragOutOfBounds) {
          console.debug('🚫 Skipping modified event for dragged entity outside bounds:', this.id);
          return;
        }
      }

      // Отправляем одно объединенное событие вместо двух отдельных
      this.notifyMetaBulletChanged('entity_modified', {
        newText: this.text,
        textLength: this.text?.length || 0,
        newLeft: this.left,
        newTop: this.top,
        newWidth: this.width,
        newHeight: this.height,
      });
    });

    // Также отслеживаем событие scaling для изменения размеров
    this.on('scaling', () => {
      console.debug('📏 TextEntity.scaling event:', {
        id: this.id,
        size: { width: this.width, height: this.height },
      });
      // При масштабировании тоже может потребоваться пересчет
    });
  }

  /**
   * Получить дефолтные стили для типа сущности
   */
  private static getDefaultStylesForType(type: TextEntityType): Partial<TextEntityOptions> {
    const baseStyles = {
      fontFamily: 'Arial, sans-serif',
      fill: '#000000',
      textAlign: 'left' as const,
      marginBottom: 16,
      marginLeft: 0,
    };

    switch (type) {
      case 'title':
        return {
          ...baseStyles,
          fontSize: 32,
          fontWeight: 'bold',
          lineHeight: 1.2,
          marginBottom: 24,
        };

      case 'subtitle':
        return {
          ...baseStyles,
          fontSize: 24,
          fontWeight: '600',
          lineHeight: 1.3,
          marginBottom: 20,
        };

      case 'heading-1':
        return {
          ...baseStyles,
          fontSize: 28,
          fontWeight: 'bold',
          lineHeight: 1.25,
          marginBottom: 22,
        };

      case 'heading-2':
        return {
          ...baseStyles,
          fontSize: 22,
          fontWeight: 'bold',
          lineHeight: 1.3,
          marginBottom: 18,
        };

      case 'heading-3':
        return {
          ...baseStyles,
          fontSize: 18,
          fontWeight: '600',
          lineHeight: 1.35,
          marginBottom: 16,
        };

      case 'bullet-level-1':
        return {
          ...baseStyles,
          fontSize: 16,
          fontWeight: 'normal',
          lineHeight: 1.4,
          marginBottom: 12,
          marginLeft: 20,
        };

      case 'bullet-level-2':
        return {
          ...baseStyles,
          fontSize: 16,
          fontWeight: 'normal',
          lineHeight: 1.4,
          marginBottom: 12,
          marginLeft: 40,
        };

      case 'bullet-level-3':
        return {
          ...baseStyles,
          fontSize: 16,
          fontWeight: 'normal',
          lineHeight: 1.4,
          marginBottom: 12,
          marginLeft: 60,
        };

      case 'bullet-level-4':
        return {
          ...baseStyles,
          fontSize: 16,
          fontWeight: 'normal',
          lineHeight: 1.4,
          marginBottom: 12,
          marginLeft: 80,
        };

      case 'bullet-level-5':
        return {
          ...baseStyles,
          fontSize: 16,
          fontWeight: 'normal',
          lineHeight: 1.4,
          marginBottom: 12,
          marginLeft: 100,
        };

      case 'paragraph':
      default:
        return {
          ...baseStyles,
          fontSize: 16,
          fontWeight: 'normal',
          lineHeight: 1.5,
          marginBottom: 16,
        };
    }
  }

  /**
   * Вычислить отступ слева для уровня буллита
   */
  private calculateIndentForLevel(level: number): number {
    return level * 20; // 20px на каждый уровень
  }

  /**
   * Проверка является ли объект TextEntity
   */
  static isTextEntity(obj: unknown): obj is TextEntity {
    return (
      obj !== null &&
      typeof obj === 'object' &&
      'type' in obj &&
      (obj.type as string)?.toLowerCase() === 'textentity' &&
      'isMetaBulletEntity' in obj &&
      obj.isMetaBulletEntity === true
    );
  }

  /**
   * Получить информацию об объекте для отладки
   */
  getDebugInfo(): string {
    return `TextEntity[${this.entityType}] id: ${this.id}, metaBullet: ${this.metaBulletId}, order: ${this.entityOrder}`;
  }

  /**
   * Изменить тип сущности с автоприменением соответствующих стилей
   * @param newEntityType - Новый тип сущности
   */
  changeType(newEntityType: TextEntityType): void {
    console.debug('🔄 TextEntity.changeType:', {
      from: this.entityType,
      to: newEntityType,
      id: this.id,
    });

    // Сохраняем старый тип для уведомления
    const oldType = this.entityType;

    // Получаем новые стили для типа
    const newStyles = TextEntity.getDefaultStylesForType(newEntityType);

    // Сохраняем текущие позицию и размеры
    const currentLeft = this.left;
    const currentTop = this.top;
    const currentWidth = this.width;
    const currentHeight = this.height;

    // Обновляем тип сущности
    this.entityType = newEntityType;

    // Применяем новые стили
    this.set({
      fontSize: newStyles.fontSize,
      fontFamily: newStyles.fontFamily,
      fontWeight: newStyles.fontWeight,
      lineHeight: newStyles.lineHeight,
      // Сохраняем позицию и размеры
      left: currentLeft,
      top: currentTop,
      width: currentWidth,
      height: currentHeight,
    });

    // Если это буллит, устанавливаем уровень и отступы
    if (isBulletType(newEntityType)) {
      this.bulletLevel = getBulletLevel(newEntityType);
      this.marginLeft = this.calculateIndentForLevel(this.bulletLevel);
    } else {
      // Если это не буллит, убираем буллит-специфичные свойства
      this.bulletLevel = undefined;
      this.marginLeft = newStyles.marginLeft || 0;
    }

    // Обновляем отступы
    this.marginBottom = newStyles.marginBottom || 16;

    // Очищаем кэш и обновляем координаты
    this._clearCache();
    this.setCoords();

    // Уведомляем метабуллит об изменении типа
    this.notifyMetaBulletChanged('type_changed', {
      oldType: oldType,
      newType: newEntityType,
      fontSize: this.fontSize,
      bulletLevel: this.bulletLevel,
      marginLeft: this.marginLeft,
      marginBottom: this.marginBottom,
    });

    console.debug('✅ TextEntity.changeType completed:', {
      newType: this.entityType,
      fontSize: this.fontSize,
      bulletLevel: this.bulletLevel,
      marginLeft: this.marginLeft,
    });
  }

  /**
   * Изменить уровень буллита (только для bullet-level-* типов)
   * @param newLevel - Новый уровень буллита (1-5)
   */
  changeBulletLevel(newLevel: number): void {
    // Проверяем что это буллит
    if (!isBulletType(this.entityType)) {
      console.warn('⚠️ changeBulletLevel called on non-bullet entity:', this.entityType);
      return;
    }

    // Валидируем уровень
    const validLevel = Math.max(1, Math.min(5, newLevel));
    const newBulletType = getBulletTypeForLevel(validLevel) as TextEntityType;

    // Сохраняем текущие позицию и размеры
    const currentLeft = this.left;
    const currentTop = this.top;
    const currentWidth = this.width;
    const currentHeight = this.height;

    // Получаем стили для нового уровня буллита
    const newStyles = TextEntity.getDefaultStylesForType(newBulletType);

    // Обновляем тип и уровень
    this.entityType = newBulletType;
    this.bulletLevel = validLevel;

    // Пересчитываем отступ
    this.marginLeft = this.calculateIndentForLevel(validLevel);

    // Применяем новые стили (в основном отступы)
    this.set({
      // НЕ меняем fontSize, fontFamily и другие стили - только отступы
      left: currentLeft,
      top: currentTop,
      width: currentWidth,
      height: currentHeight,
    });

    // Обновляем отступ снизу
    this.marginBottom = newStyles.marginBottom || 12;

    // Очищаем кэш и обновляем координаты
    this._clearCache();
    this.setCoords();

    // Уведомляем метабуллит об изменении уровня
    this.notifyMetaBulletChanged('level_changed', {
      oldLevel: this.bulletLevel,
      newLevel: validLevel,
      oldType: this.entityType,
      newType: newBulletType,
    });

    console.debug('✅ TextEntity.changeBulletLevel completed:', {
      newType: this.entityType,
      newLevel: this.bulletLevel,
      marginLeft: this.marginLeft,
    });
  }

  /**
   * Переопределение события изменения текста для интеграции с метабуллитами
   * Вызывается автоматически при изменении содержимого текста
   */
  _onTextChanged(): void {
    // Вызываем родительский метод для стандартной обработки
    if (super._onTextChanged) {
      super._onTextChanged();
    }

    console.log('📝 TextEntity._onTextChanged:', {
      id: this.id,
      entityType: this.entityType,
      metaBulletId: this.metaBulletId,
      textLength: this.text?.length || 0,
    });

    // Уведомляем метабуллит об изменении текста
    this.notifyMetaBulletChanged('text_changed', {
      newText: this.text,
      textLength: this.text?.length || 0,
    });
  }

  /**
   * Переопределение события изменения размеров для пересчета позиций
   * Вызывается при изменении размеров текстового блока
   */
  _onSizeChange(): void {
    // Проверяем есть ли родительский метод
    if (super._onSizeChange) {
      super._onSizeChange();
    }

    console.debug('📏 TextEntity._onSizeChange:', {
      id: this.id,
      width: this.width,
      height: this.height,
      metaBulletId: this.metaBulletId,
    });

    // Уведомляем метабуллит об изменении размеров
    this.notifyMetaBulletChanged('size_changed', {
      newWidth: this.width,
      newHeight: this.height,
    });
  }

  /**
   * Обработчик изменения позиции для пересчета метабуллита
   * Вызывается при перемещении объекта (drag & drop)
   */
  _onPositionChanged(): void {
    console.debug('🚚 TextEntity._onPositionChanged:', {
      id: this.id,
      position: { x: this.left, y: this.top },
      metaBulletId: this.metaBulletId,
    });

    // Уведомляем метабуллит об изменении позиции
    this.notifyMetaBulletChanged('position_changed', {
      newLeft: this.left,
      newTop: this.top,
    });
  }

  /**
   * Уведомить метабуллит об изменениях в сущности
   * @param eventType - Тип события
   * @param data - Дополнительные данные события
   */
  private notifyMetaBulletChanged(eventType: string, data?: any): void {
    if (!this.canvas) {
      console.debug('⚠️ Canvas не найден для уведомления:', this.id);
      return;
    }

    // Проверяем есть ли система MetaBulletSystem на canvas
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const system = (this.canvas as any)._metaBulletSystem;

    console.debug('🔍 Checking drag state for entity:', {
      entityId: this.id,
      eventType,
      hasSystem: !!system,
      currentDraggedEntity: system?.currentDraggedEntity?.id,
      isCurrentlyDragOutOfBounds: system?.isCurrentlyDragOutOfBounds,
      shouldBlock:
        system &&
        system.currentDraggedEntity &&
        system.currentDraggedEntity.id === this.id &&
        system.isCurrentlyDragOutOfBounds &&
        eventType === 'entity_modified',
    });

    // Если сущность перетаскивается за границы своего метабуллита, не отправляем уведомления
    // Это предотвращает ненужную рекалькуляцию старого метабуллита
    if (
      system &&
      system.currentDraggedEntity &&
      system.currentDraggedEntity.id === this.id &&
      system.isCurrentlyDragOutOfBounds &&
      eventType === 'entity_modified'
    ) {
      console.debug('🚫 Suppressing notification for dragged entity outside bounds:', {
        entityId: this.id,
        eventType,
      });
      return;
    }

    // Временно исключаем объект из бекапа во время обработки метабуллитом
    // Это предотвращает создание промежуточных бекапов во время анимаций
    this.excludeFromBackup = true;

    const eventData = {
      entityId: this.id,
      metaBulletId: this.metaBulletId,
      entityType: this.entityType,
      entityOrder: this.entityOrder,
      eventType,
      timestamp: Date.now(),
      data: data || {},
    };

    console.debug('📤 Sending metaBullet event:', eventData);

    // Отправляем кастомное событие через canvas
    this.canvas.fire('metaBullet:entity:changed', eventData);
  }

  /**
   * Восстановить возможность создания бекапов для объекта
   * Вызывается после завершения обработки метабуллитом
   */
  enableBackup(): void {
    this.excludeFromBackup = false;
  }

  /**
   * Обновить иконку буллита (заглушка для будущей реализации)
   */
  private updateBulletIcon(): void {
    if (!this.bulletLevel) return;

    // TODO: Реализовать логику обновления иконок буллитов
    // Здесь будет логика добавления/обновления иконок для буллитов
    console.debug('🔮 updateBulletIcon called (not implemented yet):', {
      bulletLevel: this.bulletLevel,
      entityType: this.entityType,
    });
  }

  // Статические свойства для автоматической сериализации кастомных полей
  static customProperties = [
    'entityType',
    'metaBulletId',
    'entityOrder',
    'bulletLevel',
    'isMetaBulletEntity',
    'marginBottom',
    'marginLeft',
    'excludeFromBackup',
  ];
}
